import { FC, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import CheckIcon from '@mui/icons-material/Check';
import { Colors } from '../../styles/theme';
import { APP_CONFIG } from '../../const/APP_CONFIG';

const EDUCATION_FEATURES = [
  'Full Pro features',
  'AI diagnostics & image analysis',
  'Voice input & vehicle database',
  'Free for students & educators',
];

const EducationForm: FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [university, setUniversity] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const isValid = name.trim() && email.trim() && university.trim();

  const handleSubmit = () => {
    if (!isValid) return;

    const subject = encodeURIComponent(
      `[${APP_CONFIG.name}] Education Plan Application`
    );
    const body = encodeURIComponent(
      `Hi,\n\nI'd like to apply for the Haist Education Plan.\n\nName: ${name.trim()}\nEmail: ${email.trim()}\nUniversity / Institution: ${university.trim()}\n\nThank you.`
    );
    window.location.href = `mailto:${APP_CONFIG.contact.email}?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={3}
      alignItems={{ xs: 'center', md: 'stretch' }}
      justifyContent="center"
      width="100%"
    >
      {/* Left: Info Card */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: '#FFFFFF',
          borderRadius: '24px',
          px: { xs: 3, md: 4 },
          py: { xs: 3, md: 4 },
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: `2px solid ${Colors.blue}`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography
            sx={{
              fontSize: { xs: '14px', md: '16px' },
              fontWeight: 600,
              color: Colors.blue,
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}
          >
            Education
          </Typography>
        </Stack>

        <Box sx={{ mb: { xs: 1, md: 2 } }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ height: { xs: '40px', md: '58px' } }}
          >
            <Typography
              sx={{
                fontSize: { xs: '34px', md: '48px' },
                fontWeight: 700,
                color: Colors.blue,
                lineHeight: 1,
                letterSpacing: '-0.5px',
              }}
            >
              Free
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontSize: { xs: '11px', md: '14px' },
              fontWeight: 400,
              color: '#9E9E9E',
              height: { xs: '18px', md: '22px' },
              mt: { xs: 0, md: 0.5 },
            }}
          >
            for students & educators
          </Typography>
        </Box>

        <Stack spacing={2} sx={{ mb: 4, flex: 1 }}>
          {EDUCATION_FEATURES.map((feature, index) => (
            <Stack key={index} direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#E8F4FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <CheckIcon sx={{ fontSize: '14px', color: Colors.blue }} />
              </Box>
              <Typography
                sx={{ fontSize: '14px', fontWeight: 400, color: '#38383B' }}
              >
                {feature}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <SchoolIcon sx={{ color: Colors.blue, fontSize: '20px' }} />
          <Typography
            sx={{ fontSize: '13px', fontWeight: 400, color: '#9E9E9E' }}
          >
            Verify with your university email
          </Typography>
        </Stack>
      </Box>

      {/* Right: Application Form */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: '#FFFFFF',
          borderRadius: '24px',
          px: { xs: 3, md: 4 },
          py: { xs: 3, md: 4 },
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '18px', md: '22px' },
            fontWeight: 600,
            color: '#38383B',
            mb: 1,
          }}
        >
          Apply for Free Access
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            color: '#9E9E9E',
            mb: 3,
          }}
        >
          Fill out the form below and we'll get back to you.
        </Typography>

        {submitted && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>
            Application sent! We'll review and get back to you.
          </Alert>
        )}

        <Stack spacing={2.5} sx={{ flex: 1 }}>
          <TextField
            label="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
              },
            }}
          />
          <TextField
            label="University / Institution"
            value={university}
            onChange={(e) => setUniversity(e.target.value)}
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
              },
            }}
          />
          <TextField
            label="Email (.edu preferred)"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
              },
            }}
          />
        </Stack>

        <Button
          variant="contained"
          fullWidth
          disabled={!isValid}
          onClick={handleSubmit}
          sx={{
            mt: 3,
            backgroundColor: Colors.blue,
            color: '#FFFFFF',
            py: { xs: 1, md: 1.5 },
            borderRadius: '12px',
            fontWeight: 700,
            fontSize: '16px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#0056CC',
            },
            '&.Mui-disabled': {
              backgroundColor: '#E0E0E0',
              color: '#9E9E9E',
            },
          }}
        >
          Submit Application
        </Button>
      </Box>
    </Stack>
  );
};

export default EducationForm;
